import { Tooltip } from 'flowbite-react';
import { Link } from 'react-router-dom';

const ELLIPSIS = '…';

interface FieldProps {
  label: string;
  value: React.ReactNode;
  className?: string;
  align?: 'right' | 'left';
  link?: string;
  maxLength?: number;
}

export const hasText = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

export const renderFieldIfText = (
  label: string,
  value?: string | null,
  props?: Pick<FieldProps, 'className' | 'align' | 'link' | 'maxLength'>
) => (hasText(value) ? <Field label={label} value={value} {...props} /> : null);

const truncate = (text: string, maxLength: number): string =>
  text.length <= maxLength ? text : text.slice(0, maxLength) + ELLIPSIS;

export const Field = ({
  label,
  value,
  className = '',
  align = 'right',
  link,
  maxLength,
}: FieldProps) => {
  const isString = typeof value === 'string';
  const shouldTruncate =
    maxLength != null && isString && (value as string).length > maxLength;
  const displayValue = shouldTruncate
    ? truncate(value as string, maxLength)
    : (value ?? '-');

  const fullText = shouldTruncate && isString ? (value as string) : undefined;

  const valueContent = link ? (
    <Link className="text-primary-600 hover:text-primary-600/80" to={link}>
      {displayValue}
    </Link>
  ) : (
    <div className="font-medium truncate">{displayValue}</div>
  );

  const wrappedValue =
    fullText != null ? (
      <Tooltip content={fullText}> {valueContent}</Tooltip>
    ) : (
      valueContent
    );

  return (
    <div
      className={`${className} flex flex-col gap-1 ${
        align === 'left' ? 'items-end' : ''
      }`}
    >
      <div className="text-gray-500 text-sm">{label}</div>
      <div className="min-w-0">{wrappedValue}</div>
    </div>
  );
};
